import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import type { FC } from 'react';
import Icon from 'src/components-v2/DataDisplay/Icon/Icon';
import PlayButton from 'src/components-v2/Inputs/PlayButton/PlayButton';
import Box from 'src/components-v2/Layout/Box/Box';
import Image from 'src/components-v2/Media/Image/Image';
import LottiePlayer from 'src/components-v2/Media/LottiePlayer/LottiePlayer';
import AnimatedVideo from 'src/components-v2/Media/Video/AnimatedVideo';
import VideoPlayer from 'src/components-v2/Media/VideoPlayer/VideoPlayer';
import { isProduction } from 'src/lib/constants';
import { byVideoType, makeImageSource } from 'src/lib/utils';
import type { CmsMediaSwitchProps } from './CmsMediaSwitch.types';

const CmsMediaSwitch: FC<CmsMediaSwitchProps> = ({
  asset,
  height,
  sources,
  sx,
  sys,
  queryString = {
    xs: '',
    sm: '',
    md: '',
    lg: '',
    xl: '',
  },
  width,
  ...rest
}) => {
  const inspectorProps = useContentfulInspectorMode({ entryId: sys?.id });

  switch (asset?.appearance) {
    case 'Image':
      const { lgImgSrc, mdImgSrc, smImgSrc } = makeImageSource(asset);
      return (
        <Image
          isLazy={asset?.isLazy || false}
          sx={{ ...sx, ...asset?.styles }}
          width={asset?.width || width}
          height={asset?.height || height}
          src={lgImgSrc + (queryString?.xl ?? queryString?.lg ?? '')}
          alt={asset?.description ?? asset?.title ?? ''}
          sources={
            sources ?? {
              xs: smImgSrc + (queryString?.xs ?? ''),
              sm: smImgSrc + (queryString?.sm ?? ''),
              md: mdImgSrc + (queryString?.md ?? ''),
              lg: lgImgSrc + (queryString?.lg ?? ''),
              ...(queryString?.xl && { xl: lgImgSrc + queryString.xl }),
            }
          }
          {...inspectorProps({ fieldId: 'internalTitle' })}
          {...rest}
        />
      );
    case 'Video':
      const sortedVideos = asset?.video?.sort(byVideoType);
      return (
        <AnimatedVideo
          isLazy={asset?.isLazy || false}
          width={asset?.width || width}
          height={asset?.height || height}
          sx={{ ...sx, ...asset?.styles }}
          sources={sortedVideos?.map((asset) => {
            return {
              src: asset?.fields?.file?.url,
              type: asset?.fields?.file?.contentType,
            };
          })}
          {...inspectorProps({ fieldId: 'internalTitle' })}
        />
      );
    case 'Lottie':
      const src = asset?.lottie?.fields?.file?.url;
      return (
        <Box
          sx={{ ...sx, ...asset?.styles }}
          {...inspectorProps({ fieldId: 'internalTitle' })}
        >
          <LottiePlayer
            src={src}
            autoplay={true}
            loop={true}
            style={{
              width: '100%',
              height: 'auto',
              margin: '0 auto',
            }}
          />
        </Box>
      );
    case 'Embedded':
      const coverImg = asset?.coverImage?.fields?.file?.url;

      return (
        <VideoPlayer
          sx={{ ...sx, ...asset?.styles }}
          isLazy={asset?.isLazy || false}
          url={asset?.url}
          light={coverImg ? `${coverImg + '?fm=webp&w=1364&q=60'}` : true}
          playing={true}
          playIcon={
            <PlayButton
              playIcon={<Icon icon='ChevronRight' />}
              sx={{
                '& svg': {
                  zoom: 2,
                },
                '&:hover, :focus': {
                  '& svg': {
                    color: 'white',
                  },
                  backgroundColor: 'black',
                },
              }}
            />
          }
          {...inspectorProps({ fieldId: 'internalTitle' })}
        />
      );

    default:
      if (!isProduction) {
        return (
          <>
            <Image
              width={600}
              height={400}
              src='https://placehold.co/600x400'
              alt={asset?.description}
              {...inspectorProps({ fieldId: 'internalTitle' })}
            />
          </>
        );
      } else {
        return null;
      }
  }
};

export default CmsMediaSwitch;
